import { useQuery } from '@tanstack/react-query';
import { Alert, Empty, Flex, Skeleton, Tag, Timeline, Typography } from 'antd';
import { api } from '../../api';
import { ACAO_LABEL, formatarData } from '../../dominio';

/**
 * Quem mexeu neste produto, e no quê.
 *
 * Lê a mesma trilha da tela de Trilha, recortada por este produto - e é por
 * isso que as ações de preço são gravadas com o id do **produto**: pendurada no
 * preço, a mudança de valor sumiria do histórico junto com a linha que ela
 * substituiu, que é exatamente o momento em que alguém vem procurá-la.
 *
 * Sem paginação de propósito: cinquenta linhas cobrem anos de um catálogo que
 * muda de preço uma ou duas vezes por ano, e quem precisar de mais tem a tela
 * de Trilha com filtro por período.
 */
export function AlteracoesDoProduto({ productId }: { productId: string }) {
  const trilha = useQuery({
    queryKey: ['trilha-do-produto', productId],
    queryFn: () =>
      api.listActivity({ entityType: 'product', entityId: productId, page: 1, pageSize: 50 }),
  });

  if (trilha.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;

  if (trilha.error) {
    return <Alert type="error" showIcon message={(trilha.error as Error).message} />;
  }

  const linhas = trilha.data?.items ?? [];

  if (linhas.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Nada registrado ainda. Toda mudança de preço, de nome ou do que a compra libera aparece aqui."
      />
    );
  }

  return (
    <Timeline
      items={linhas.map((linha) => ({
        color: linha.action.includes('excluiu') ? 'red' : 'blue',
        children: (
          <Flex vertical gap={2}>
            <Tag style={{ marginInlineEnd: 0, alignSelf: 'flex-start' }}>
              {ACAO_LABEL[linha.action] ?? linha.action}
            </Tag>
            <Typography.Text>{linha.summary}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {formatarData(linha.createdAt)} ·{' '}
              {/* Conta excluída preserva a autoria sem o nome, como no histórico. */}
              {linha.actorName ?? (linha.actorId ? 'conta excluída' : 'automático')}
            </Typography.Text>
          </Flex>
        ),
      }))}
    />
  );
}
